import React, { useEffect, useRef } from 'react';

const ArrowIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" width="12" height="12" aria-hidden="true">
    <path d="M5 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const layerConfigs = {
  back: { count: 5, scale: 0.72, node: 2.2, alpha: 0.22, parallax: 14 },
  middle: { count: 9, scale: 0.92, node: 2.8, alpha: 0.48, parallax: 28 },
  front: { count: 7, scale: 1.14, node: 3.8, alpha: 1, parallax: 48 },
};

const nodeSeeds = [
  // upper band
  ['back', 0.14, 0.28], ['back', 0.32, 0.18], ['back', 0.58, 0.24], ['back', 0.78, 0.15], ['back', 0.88, 0.38],
  ['back', 0.04, 0.12], ['back', 0.44, 0.10],
  ['middle', 0.62, 0.20],
  ['front', 0.26, 0.34],
  // mid band
  ['middle', 0.10, 0.56], ['middle', 0.24, 0.43], ['middle', 0.36, 0.64], ['middle', 0.48, 0.38], ['middle', 0.58, 0.57],
  ['middle', 0.70, 0.32], ['middle', 0.78, 0.64], ['middle', 0.88, 0.50], ['middle', 0.94, 0.72],
  ['back', 0.02, 0.48], ['back', 0.42, 0.34],
  ['middle', 0.14, 0.36], ['middle', 0.54, 0.46],
  ['front', 0.40, 0.44], ['front', 0.64, 0.38],
  // mid-front
  ['front', 0.16, 0.76], ['front', 0.30, 0.55], ['front', 0.44, 0.82], ['front', 0.56, 0.48],
  ['front', 0.68, 0.74], ['front', 0.82, 0.46], ['front', 0.92, 0.82],
  ['back', 0.08, 0.68], ['back', 0.52, 0.72],
  ['middle', 0.28, 0.74], ['middle', 0.62, 0.78],
  ['front', 0.36, 0.60],
  // lower band — extends coverage into bottom of hero
  ['back', 0.18, 0.92], ['back', 0.46, 0.96], ['back', 0.72, 0.90], ['back', 0.96, 0.94],
  ['middle', 0.08, 0.84], ['middle', 0.34, 0.88], ['middle', 0.56, 0.92], ['middle', 0.76, 0.86], ['middle', 0.90, 0.96],
  ['front', 0.22, 1.00], ['front', 0.50, 0.94], ['front', 0.70, 1.00], ['front', 0.86, 0.88],
  ['back', 0.36, 0.90], ['back', 0.60, 0.96], ['back', 0.84, 0.94],
  ['middle', 0.20, 0.92], ['middle', 0.44, 0.84],
  ['front', 0.06, 0.96], ['front', 0.60, 0.86],
];

function buildGraph() {
  const nodes = nodeSeeds.map(([layer, x, y], index) => ({
    id: index,
    layer,
    x,
    y,
    phase: index * 1.91,
    wobble: 0.004 + (index % 5) * 0.0012,
    freq: 0.55 + (index % 7) * 0.08,
  }));

  const edgeMap = new Map();
  nodes.forEach((node) => {
    const neighbors = nodes
      .filter((candidate) => candidate.id !== node.id)
      .map((candidate) => {
        const dx = candidate.x - node.x;
        const dy = candidate.y - node.y;
        const layerPenalty = candidate.layer === node.layer ? 0 : 0.035;
        return { id: candidate.id, distance: Math.hypot(dx, dy) + layerPenalty };
      })
      .sort((a, b) => a.distance - b.distance)
      .slice(0, node.id % 3 === 0 ? 3 : 2);

    neighbors.forEach(({ id }) => {
      const key = [node.id, id].sort((a, b) => a - b).join('-');
      edgeMap.set(key, [node.id, id]);
    });
  });

  return { nodes, edges: Array.from(edgeMap.values()) };
}

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  const value = parseInt(normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function NetworkGraphCanvas({ accent = '#7c5cff' }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const ctx = canvas.getContext('2d');
    const { nodes, edges } = buildGraph();
    const packets = Array.from({ length: 30 }, (_, index) => ({
      edgeIndex: index % edges.length,
      progress: (index * 0.071) % 1,
      speed: 0.10 + (index % 5) * 0.018,
      forward: index % 2 === 0,
    }));
    const pulses = [];
    const accentRgb = hexToRgb(accent);
    const accentRgba = (alpha) => `rgba(${accentRgb.r},${accentRgb.g},${accentRgb.b},${alpha})`;
    const pointer = { targetX: 0, targetY: 0, x: 0, y: 0, glowX: 0.68, glowY: 0.34 };
    let width = 0;
    let height = 0;
    let dpr = 1;
    let rafId;
    let lastTime = performance.now();

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const handlePointerMove = (event) => {
      const rect = wrap.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      if (x < 0 || x > 1 || y < 0 || y > 1) {
        pointer.targetX = 0;
        pointer.targetY = 0;
        pointer.glowX = 0.68;
        pointer.glowY = 0.34;
        return;
      }

      pointer.targetX = (x - 0.5) * 2;
      pointer.targetY = (y - 0.5) * 2;
      pointer.glowX = x;
      pointer.glowY = y;
    };

    const project = (node, time) => {
      const layer = layerConfigs[node.layer];
      const centerX = width * 0.56;
      const centerY = height * 0.46;
      const spreadX = width * 0.86 * layer.scale;
      const spreadY = height * 0.62 * layer.scale;
      const wobbleX = Math.sin(time * node.freq + node.phase) * width * node.wobble;
      const wobbleY = Math.cos(time * (node.freq * 0.82) + node.phase) * height * node.wobble;

      return {
        x: centerX + (node.x - 0.5) * spreadX + pointer.x * layer.parallax + wobbleX,
        y: centerY + (node.y - 0.5) * spreadY + pointer.y * layer.parallax * 0.62 + wobbleY,
        radius: layer.node * layer.scale,
        alpha: layer.alpha,
        layer: node.layer,
      };
    };

    const drawGrid = (time) => {
      const grid = 38;
      const offset = (time * 14) % grid;
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.035)';
      ctx.lineWidth = 1;

      for (let x = -grid + offset; x < width + grid; x += grid) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + width * 0.045, height);
        ctx.stroke();
      }

      for (let y = -grid + offset; y < height + grid; y += grid) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.restore();
    };

    const draw = (now) => {
      const delta = Math.min(0.04, (now - lastTime) / 1000);
      const time = now / 1000;
      lastTime = now;

      pointer.x += (pointer.targetX - pointer.x) * 0.055;
      pointer.y += (pointer.targetY - pointer.y) * 0.055;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#08090a';
      ctx.fillRect(0, 0, width, height);

      drawGrid(time);

      const glow = ctx.createRadialGradient(
        pointer.glowX * width,
        pointer.glowY * height,
        0,
        pointer.glowX * width,
        pointer.glowY * height,
        width * 0.48
      );
      glow.addColorStop(0, accentRgba(0.20));
      glow.addColorStop(0.35, accentRgba(0.08));
      glow.addColorStop(1, accentRgba(0));
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, width, height);

      const points = nodes.map((node) => project(node, time));

      edges.forEach(([from, to]) => {
        const a = points[from];
        const b = points[to];
        const alpha = Math.min(a.alpha, b.alpha) * 0.32;
        ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      });

      packets.forEach((packet, index) => {
        packet.progress += packet.speed * delta;
        if (packet.progress >= 1) {
          packet.progress %= 1;
          const edge = edges[packet.edgeIndex];
          pulses.push({ node: packet.forward ? edge[1] : edge[0], age: 0 });
          packet.edgeIndex = (packet.edgeIndex + 5 + index) % edges.length;
          packet.forward = !packet.forward;
        }

        const [startId, endId] = edges[packet.edgeIndex];
        const a = points[packet.forward ? startId : endId];
        const b = points[packet.forward ? endId : startId];
        const x = a.x + (b.x - a.x) * packet.progress;
        const y = a.y + (b.y - a.y) * packet.progress;
        const trailProgress = Math.max(0, packet.progress - 0.12);
        const tx = a.x + (b.x - a.x) * trailProgress;
        const ty = a.y + (b.y - a.y) * trailProgress;

        const trail = ctx.createLinearGradient(tx, ty, x, y);
        trail.addColorStop(0, accentRgba(0));
        trail.addColorStop(1, accentRgba(0.65));
        ctx.strokeStyle = trail;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(x, y);
        ctx.stroke();

        ctx.shadowColor = accent;
        ctx.shadowBlur = 12;
        ctx.fillStyle = accent;
        ctx.beginPath();
        ctx.arc(x, y, 2.15, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      for (let index = pulses.length - 1; index >= 0; index -= 1) {
        const pulse = pulses[index];
        pulse.age += delta;
        const point = points[pulse.node];
        const progress = pulse.age / 0.72;
        if (progress >= 1) {
          pulses.splice(index, 1);
          continue;
        }

        ctx.strokeStyle = accentRgba((1 - progress) * 0.36);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(point.x, point.y, point.radius + progress * 20, 0, Math.PI * 2);
        ctx.stroke();
      }

      points.forEach((point) => {
        const isFront = point.layer === 'front';
        ctx.shadowColor = isFront ? accent : 'rgba(255,255,255,0.35)';
        ctx.shadowBlur = isFront ? 18 : 5;
        ctx.fillStyle = isFront ? accent : `rgba(255,255,255,${point.alpha})`;
        ctx.beginPath();
        ctx.arc(point.x, point.y, point.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        ctx.strokeStyle = isFront ? 'rgba(255,255,255,0.38)' : `rgba(255,255,255,${point.alpha * 0.5})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(point.x, point.y, point.radius + 4, 0, Math.PI * 2);
        ctx.stroke();
      });

      rafId = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('resize', resize);
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('resize', resize);
    };
  }, [accent]);

  return (
    <div className="hero-network" ref={wrapRef} aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}

export default function Hero() {
  return (
    <section className="hero" id="top">
      <NetworkGraphCanvas />
      <div className="hero-vignette" aria-hidden="true" />
      <div className="hero-readout hero-readout-tl" aria-hidden="true">// Backend · AI · Products</div>
      <div className="hero-readout hero-readout-tr" aria-hidden="true">Buenos Aires, ARG</div>

      <div className="container hero-content">
        <div className="rise">
          <div className="availability-badge">
            <span className="availability-dot" />
            <span>Open to senior backend / fullstack roles</span>
          </div>
        </div>

        <h1 className="rise rise-1">
          Backend &amp; fullstack engineer<br />
          <span className="accent">building scalable systems,<br />AI infrastructure and internet products.</span>
        </h1>

        <p className="hero-sub rise rise-2">
          I&apos;m Gabriel — I design and ship APIs, distributed systems and the consumer-facing surfaces
          that sit on top of them. Based in Buenos Aires, building for the web.
        </p>

        <div className="hero-actions rise rise-3">
          <a className="btn btn-accent" href="#contact">
            Start a project <ArrowIcon />
          </a>
          <a className="btn btn-secondary" href="#products">
            See selected work
          </a>
        </div>

        <div className="hero-meta rise rise-4">
          <div className="hero-meta-item">
            <span className="hero-meta-label">Based in</span>
            <span className="hero-meta-value">Buenos Aires, Argentina</span>
          </div>
          <div className="hero-meta-item">
            <span className="hero-meta-label">Focus</span>
            <span className="hero-meta-value">Backend APIs · AI systems · Full stack</span>
          </div>
          <div className="hero-meta-item">
            <span className="hero-meta-label">Experience</span>
            <span className="hero-meta-value">5+ yrs · Products + Enterprise</span>
          </div>
          <div className="hero-meta-item">
            <span className="hero-meta-label">Currently</span>
            <span className="hero-meta-value">Available · Open to new projects</span>
          </div>
        </div>
      </div>
    </section>
  );
}
